import ListFilterPage from '@/pages/ListFilterPage';
import { useServices } from '@/components/ServiceContext';
import React from 'react';
import {
  sortBooleans,
  sortMonetaryRanges,
  sortRating,
  sortSalesStage,
} from '@lambda-layer/utils/sort.ts';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import {
  FilterSelections,
  RelationalOperator,
} from '@lambda-layer/types/filter.ts';

import { ControlType } from '@lambda-layer/models/FilterDefinition/FilterDefinitionFields.ts';

export const houseListFilters = FilterDefinition.createFilterDefinitions(
  [
    {
      label: 'Top n LTS Score',
      controlType: ControlType.TEXT,
      databaseMetadata: {
        columnName: 'ltsScore',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'limit',
        columnOrder: 0,
        columnName: 'ltsScore',
      },
      defaultValue: [100_000],
    },
    // Exclude opt-out records
    {
      label: 'Opt Out',
      controlType: ControlType.BOOLEAN_TOGGLE,
      isAdvancedFilter: true,
      databaseMetadata: {
        columnName: 'optOut',
        dataType: 'boolean',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'Opt Out',
      },
      defaultValue: [true],
      defaultRelationalOperator: RelationalOperator.NOT_EQUAL,
    },
    // Exclude mlActive = 'false' records
    {
      label: 'ML Active',
      controlType: ControlType.MULTI_SELECT,
      isAdvancedFilter: true,
      databaseMetadata: {
        columnName: 'mlInactiveReasons',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'ML Inactive Flags',
      },
      isArrayField: true,
      defaultRelationalOperator: RelationalOperator.CONTAINS_NONE,
      availableOperators: [
        RelationalOperator.CONTAINS_ANY,
        RelationalOperator.CONTAINS_NONE,
        RelationalOperator.CONTAINS_ALL,
      ],
    },
    {
      label: 'LOC',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'loc',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 29,
        columnName: 'LOC',
      },
      staffSeedMetadata: {
        dbColumnName: 'loc',
      },
    },
    {
      label: 'Opportunity Site',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'opportunitySite',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 1,
        columnName: 'OppCommunity',
      },
      staffSeedMetadata: {
        dbColumnName: 'community',
      },
      toolTip: 'Opportunity Site is used for all projects except Tribune',
    },
    {
      label: 'Primary Site',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'primarySite',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 34,
        columnName: 'PrimarySite',
      },
      toolTip: 'Primary Site is used for Tribune only',
    },
    {
      label: 'Sales Stage',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'salesStage',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 2,
        columnName: 'OppStage',
      },
      staffSeedMetadata: {
        staticValue: 'Staff Seed',
      },
      valueSortFn: sortSalesStage,
    },
    {
      label: 'LTS Grade',
      controlType: ControlType.MULTI_SELECT,
      availableOperators: [
        RelationalOperator.LESS_THAN_OR_EQUAL,
        RelationalOperator.GREATER_THAN_OR_EQUAL,
        RelationalOperator.EQUAL,
      ],
      databaseMetadata: {
        columnName: 'ltsGrade',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 16,
        columnName: 'LTSGrade',
      },
      toolTip: 'Likely To Settle model score updated weekly by Acxiom ',
    },
    {
      label: 'EPM Grade',
      controlType: ControlType.MULTI_SELECT,
      availableOperators: [
        RelationalOperator.LESS_THAN_OR_EQUAL,
        RelationalOperator.GREATER_THAN_OR_EQUAL,
        RelationalOperator.EQUAL,
      ],
      databaseMetadata: {
        columnName: 'epm2Grade',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 30,
        columnName: 'EPM2Grade',
      },
      toolTip:
        'Used secondarily on HL, Enterprise Prospect Model score by Acxiom applied quarterly',
    },
    {
      label: 'State',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'state',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 9,
        columnName: 'State',
      },
      staffSeedMetadata: {
        dbColumnName: 'state',
      },
    },
    {
      label: 'Distance to Site',
      controlType: ControlType.TEXT,
      availableOperators: [
        RelationalOperator.LESS_THAN_OR_EQUAL,
        RelationalOperator.GREATER_THAN_OR_EQUAL,
      ],
      databaseMetadata: {
        columnName: 'distanceToSite',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 25,
        columnName: 'DistanceToSite',
      },
      defaultRelationalOperator: RelationalOperator.LESS_THAN_OR_EQUAL,
    },
    {
      label: 'Zip5',
      controlType: ControlType.SEARCHABLE_MULTI_SELECT,
      databaseMetadata: {
        columnName: 'zip5',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 28,
        columnName: 'Zip5',
      },
    },
    {
      label: 'ZIP+4',
      controlType: ControlType.SEARCHABLE_MULTI_SELECT,
      databaseMetadata: {
        columnName: 'zipcode',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 10,
        columnName: 'ZipCode',
      },
      staffSeedMetadata: {
        dbColumnName: 'postalCode',
      },
    },
    {
      label: 'HoH Home Value',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'homeValue',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 18,
        columnName: 'HomeMarketValue',
      },
      valueSortFn: sortMonetaryRanges,
    },
    {
      label: 'HoH Income',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'hohIncome',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 19,
        columnName: 'IncomeLevel',
      },
      valueSortFn: sortMonetaryRanges,
    },
    {
      label: 'HoH Net Worth',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'hohNetWorth',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 20,
        columnName: 'NetWorth',
      },
      valueSortFn: sortMonetaryRanges,
    },
    {
      label: 'Sales Option Date',
      controlType: ControlType.DATE_RANGE,
      databaseMetadata: {
        columnName: 'salesOptionDate',
        dataType: 'date',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 32,
        columnName: 'SalesOptionDate',
      },
    },
    {
      label: 'Priority Date',
      controlType: ControlType.DATE_RANGE,
      databaseMetadata: {
        columnName: 'priorityDate',
        dataType: 'date',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 33,
        columnName: 'PriorityDate',
      },
    },
    {
      label: 'Contact Type',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'contactType',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 23,
        columnName: 'CConType',
      },
    },
    {
      label: 'HoH Age',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'hohAge',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 26,
        columnName: 'HOHAge',
      },
    },
    {
      label: 'Marital Status',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'maritalStatus',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 35,
        columnName: 'MaritalStatus',
      },
    },
    {
      label: 'Gender',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'gender',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 27,
        columnName: 'Gender',
      },
      labelMapping: { Male: 'M', Female: 'F' },
    },
    {
      label: 'Segmentation',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'segmentation',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 36,
        columnName: 'Segmentation',
      },
    },
    {
      label: 'Rating',
      controlType: ControlType.MULTI_SELECT,
      databaseMetadata: {
        columnName: 'rating',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 37,
        columnName: 'Rating',
      },
      valueSortFn: sortRating,
    },
    {
      databaseMetadata: {
        columnName: 'apartmentName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 40,
        columnName: 'ApartmentName',
      },
    },
    {
      label: 'Batch Code',
      controlType: ControlType.TEXT,
      csvMetadata: {
        fieldType: 'static',
        columnOrder: 11,
        columnName: 'BatchCode',
      },
    },
    {
      label: 'Drop Date',
      controlType: ControlType.DATE,
      csvMetadata: {
        dateFormat: 'MM/dd/yyyy',
        fieldType: 'static',
        columnOrder: 24,
        columnName: 'DropDate',
      },
    },
    {
      label: 'Staff Seed',
      controlType: ControlType.SELECT,
      csvMetadata: {
        fieldType: 'static',
        columnOrder: 0,
        columnName: 'StaffSeed',
      },
      defaultOptions: ['0', '1'],
      defaultValue: ['0'],
      labelMapping: { '0': 'No', '1': 'Yes' },
      valueSortFn: sortBooleans,
    },
    {
      databaseMetadata: {
        columnName: 'qasValidationStatus',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 38,
        columnName: 'QASValidationStatus',
      },
    },
    {
      databaseMetadata: {
        columnName: 'labelSalutation',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 3,
        columnName: 'LabelSalutation',
      },
      staffSeedMetadata: {
        dbColumnName: 'labelSalutation',
      },
    },
    {
      databaseMetadata: {
        columnName: 'letterSalutation',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 4,
        columnName: 'LetterSalutation',
      },
      staffSeedMetadata: {
        dbColumnName: 'letterSalutation',
      },
    },
    {
      databaseMetadata: {
        columnName: 'firstName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 5,
        columnName: 'FirstName',
      },
      staffSeedMetadata: {
        dbColumnName: 'firstName',
      },
    },
    {
      databaseMetadata: {
        columnName: 'lastName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 6,
        columnName: 'LastName',
      },
      staffSeedMetadata: {
        dbColumnName: 'lastName',
      },
    },
    {
      databaseMetadata: {
        columnName: 'address',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 7,
        columnName: 'Address',
      },
      staffSeedMetadata: {
        dbColumnName: 'address',
      },
    },
    {
      databaseMetadata: {
        columnName: 'city',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 8,
        columnName: 'City',
      },
      staffSeedMetadata: {
        dbColumnName: 'city',
      },
    },
    {
      databaseMetadata: {
        columnName: 'accountID',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 12,
        columnName: 'AccountSourceID',
      },
    },
    {
      databaseMetadata: {
        columnName: 'contactId',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 13,
        columnName: 'ContactSourceID',
      },
      staffSeedMetadata: {
        dbColumnName: 'id',
      },
    },
    {
      databaseMetadata: {
        columnName: 'opportunityId',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 14,
        columnName: 'OpportunitySourceID',
      },
    },
    {
      databaseMetadata: {
        columnName: 'groupName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 15,
        columnName: 'GroupName',
      },
      staffSeedMetadata: {
        staticValue: 'StaffSeed',
      },
    },
    {
      databaseMetadata: {
        columnName: 'ltsScore',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 17,
        columnName: 'LTSScore',
      },
    },
    {
      databaseMetadata: {
        columnName: 'birthDate',
        dataType: 'date',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 21,
        columnName: 'DateOfBirth',
      },
    },
    {
      databaseMetadata: {
        columnName: 'primaryContact',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 22,
        columnName: 'IsPrimaryContact',
        valueMapping: { true: 'T', false: 'F' },
      },
    },
    {
      databaseMetadata: {
        columnName: 'returnedMail',
        dataType: 'boolean',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 39,
        columnName: 'ReturnedMail',
        valueMapping: { true: 'T', false: 'F' },
      },
    },
    {
      databaseMetadata: {
        columnName: 'epm2Score',
        dataType: 'number',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 31,
        columnName: 'EPM2Score',
      },
    },
    {
      databaseMetadata: {
        columnName: 'mlContactInactiveReasons',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'ML Contact Inactive Reasons',
      },
    },
    {
      databaseMetadata: {
        columnName: 'mlSOInactiveReasons',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'ML SO Inactive Reasons',
      },
    },
    {
      databaseMetadata: {
        columnName: 'priorityType',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 41,
        columnName: 'PriorityType',
      },
    },
    {
      databaseMetadata: {
        columnName: 'nextStep',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 42,
        columnName: 'NextStep',
      },
    },
    {
      // Report (export) + Data Explorer: show the counselor's name under the
      // existing "SalesCounselor" column instead of the Salesforce record ID.
      databaseMetadata: {
        columnName: 'salesCounselorName',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 43,
        columnName: 'SalesCounselor',
      },
    },
    {
      // Data Explorer only: keep the raw Salesforce ID visible. columnOrder 0
      // excludes it from the report/export (getColumnHeaders filters > 0) while
      // the Data Explorer grid includes zero-order fields.
      databaseMetadata: {
        columnName: 'salesCounselor',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 0,
        columnName: 'SalesCounselorId',
      },
    },
    {
      databaseMetadata: {
        columnName: 'timeframe',
        dataType: 'string',
      },
      csvMetadata: {
        fieldType: 'database',
        columnOrder: 44,
        columnName: 'Timeframe',
      },
    },
  ],
  '1.2.4'
);

const exportJobName = (filterSelections: FilterSelections) => {
  const locField = houseListFilters.find(f => f.label === 'LOC');
  if (locField) {
    const selections = filterSelections[locField.id];
    const selectionValues = selections.value.filter(Boolean);
    if (selectionValues.length) {
      return selectionValues.join(', ');
    }
  }
  return `House List Export ${new Date().toISOString()}`;
};

const HouseListFilterPage: React.FC = () => {
  const { houseListService } = useServices();

  return (
    <ListFilterPage
      exportJobName={exportJobName}
      listType={'houselist'}
      listService={houseListService}
      filterDefinitions={houseListFilters}
    />
  );
};

export default HouseListFilterPage;
